import { useEffect, useMemo, useState } from "react";
import {
  AlertCircle,
  Loader2,
  Car,
  ShowerHead,
  Coffee,
  Utensils,
  ShoppingBag,
  Wifi,
  Shirt,
  Lock,
  Store,
  Toilet,
  Snowflake,
  Armchair,
  Accessibility,
  type LucideIcon,
} from "lucide-react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { Alert, AlertDescription, Button, Checkbox } from "@sportscanner/ui";
import {
  CLUB_AMENITIES,
  clubAmenitiesSchema,
  type ClubAmenity,
} from "@sportscanner/schemas";
import { apiGet, apiPut, getApiErrorCode } from "@/lib/api";

const AMENITY_ICONS: Record<ClubAmenity, LucideIcon> = {
  PARKING: Car,
  SHOWERS: ShowerHead,
  CAFE_BAR: Coffee,
  FOOD: Utensils,
  GEAR_RENTAL: ShoppingBag,
  WIFI: Wifi,
  CHANGING_ROOMS: Shirt,
  LOCKERS: Lock,
  PRO_SHOP: Store,
  TOILETS: Toilet,
  AIR_CONDITIONING: Snowflake,
  SPECTATOR_SEATING: Armchair,
  WHEELCHAIR_ACCESS: Accessibility,
};

interface ClubAmenitiesData {
  id: string;
  amenities: ClubAmenity[];
}

interface Props {
  clubId: string;
}

export default function AmenitiesForm({ clubId }: Props) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [errorCode, setErrorCode] = useState<string | null>(null);
  const [selected, setSelected] = useState<Set<ClubAmenity>>(new Set());

  const clubQuery = useQuery({
    queryKey: ["admin", "clubs", clubId, "amenities"],
    queryFn: async () => {
      const data = await apiGet<{ club: ClubAmenitiesData }>(
        `/api/admin/clubs/${clubId}`,
      );
      return data.club;
    },
  });
  const club = clubQuery.data;

  useEffect(() => {
    if (!club) return;
    setSelected(new Set(club.amenities));
    setErrorCode(null);
  }, [club]);

  const isDirty = useMemo(() => {
    if (!club) return false;
    const persisted = new Set(club.amenities);
    if (persisted.size !== selected.size) return true;
    for (const key of selected) if (!persisted.has(key)) return true;
    return false;
  }, [club, selected]);

  const mutation = useMutation({
    mutationFn: async () => {
      if (!club) throw new Error("no club");
      // Persist in catalogue order so reads always come back consistent —
      // matches how the grid + the public web row render.
      const amenities = CLUB_AMENITIES.filter((key) => selected.has(key));
      const parsed = clubAmenitiesSchema.safeParse({ amenities });
      if (!parsed.success) {
        throw {
          error: parsed.error.issues[0]?.message ?? "VALIDATION_FAILED",
        };
      }
      await apiPut(`/api/admin/clubs/${club.id}/amenities`, parsed.data);
    },
    onSuccess: async () => {
      setErrorCode(null);
      toast.success(t("amenities.savedToast"));
      await queryClient.invalidateQueries({
        queryKey: ["admin", "clubs", clubId],
      });
    },
    onError: (err) => {
      setErrorCode(getApiErrorCode(err));
    },
  });

  if (clubQuery.isLoading) {
    return (
      <div className="flex items-center justify-center py-16">
        <Loader2 className="w-6 h-6 animate-spin text-neutral-400" />
      </div>
    );
  }
  if (clubQuery.isError || !club) {
    return (
      <Alert variant="destructive">
        <AlertCircle className="h-4 w-4" />
        <AlertDescription>{t("errors.api.INTERNAL_ERROR")}</AlertDescription>
      </Alert>
    );
  }

  function toggle(key: ClubAmenity) {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  }

  return (
    <div className="space-y-6">
      {errorCode && (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>{t(`errors.api.${errorCode}`)}</AlertDescription>
        </Alert>
      )}

      <section className="bg-white rounded-xl border border-neutral-200 p-5 space-y-4">
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
          {CLUB_AMENITIES.map((key) => {
            const Icon = AMENITY_ICONS[key];
            const isSelected = selected.has(key);
            const inputId = `amenity-${key}`;
            return (
              <label
                key={key}
                htmlFor={inputId}
                className={`flex flex-col items-start gap-2 rounded-lg border p-3 transition-colors cursor-pointer ${
                  isSelected
                    ? "border-primary-300 bg-primary-50/40"
                    : "border-neutral-200 hover:border-neutral-300"
                } ${mutation.isPending ? "opacity-60 cursor-not-allowed" : ""}`}
              >
                <div className="flex w-full items-center justify-between">
                  <Icon className="w-5 h-5 text-primary-600" />
                  <Checkbox
                    id={inputId}
                    checked={isSelected}
                    onCheckedChange={() => toggle(key)}
                    disabled={mutation.isPending}
                  />
                </div>
                <span className="text-sm font-medium text-neutral-800">
                  {t(`amenities.labels.${key}`)}
                </span>
              </label>
            );
          })}
        </div>
      </section>

      <div className="flex justify-end">
        <Button
          type="button"
          onClick={() => mutation.mutate()}
          disabled={!isDirty || mutation.isPending}
          className="bg-primary-600 hover:bg-primary-700 text-white"
        >
          {mutation.isPending ? (
            <Loader2 className="w-4 h-4 animate-spin" />
          ) : (
            t("common.save")
          )}
        </Button>
      </div>
    </div>
  );
}
